import { router } from '@app/providers/router/router-provider';
import { ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import ruRU from 'antd/locale/ru_RU';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@shared/redux';
import { appSettingsSelectors } from '@shared/redux/ducks/app-settings';
import {
  CHANNEL_FORM_DRAFT_TTL_MS,
  channelFormDraftsActions,
} from '@shared/redux/ducks/channel-form-drafts';
import { AppTheme } from '@shared/services/theme';
import { createTheme } from '@shared/style/theme';
import { GlobalConfirmModal } from '../global-confirm-modal/global-confirm-modal';
import { MessageProvider } from '../message/message';
import { NotificationProvider } from '../notification/notification';

export const AppContentProvider = () => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const selectedLanguage = useAppSelector(appSettingsSelectors.selectLanguage);
  const selectedTheme = useAppSelector(appSettingsSelectors.selectTheme);
  const language = selectedLanguage || i18n.language;
  const themeMode = selectedTheme === AppTheme.dark ? AppTheme.dark : AppTheme.light;
  const isRussian = language === 'ru';
  const antdLocale = isRussian ? ruRU : enUS;
  const antdTheme = useMemo(() => createTheme(themeMode), [themeMode]);

  useEffect(() => {
    if (i18n.language !== language) {
      void i18n.changeLanguage(language);
    }
  }, [i18n, language]);

  useEffect(() => {
    dayjs.locale(isRussian ? 'ru' : 'en');
  }, [isRussian]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
    document.documentElement.style.colorScheme = themeMode;
  }, [themeMode]);

  useEffect(() => {
    dispatch(channelFormDraftsActions.cleanupExpired(undefined));

    const intervalId = window.setInterval(() => {
      dispatch(channelFormDraftsActions.cleanupExpired(undefined));
    }, CHANNEL_FORM_DRAFT_TTL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dispatch]);

  return (
    <ConfigProvider locale={antdLocale} theme={antdTheme}>
      <NotificationProvider>
        <MessageProvider>
          <GlobalConfirmModal />
          <RouterProvider router={router} />
          <div id="root-portal" />
        </MessageProvider>
      </NotificationProvider>
    </ConfigProvider>
  );
};
